import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import recetaService, { RecetaListItemDto } from '../../services/recetaService';
import { formatCurrency, formatDateTime } from '../../utils/formatters';

const Recetas: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<RecetaListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (pageNum: number, pageSize: number, search: string) => {
      try {
        setLoading(true);
        const data = await recetaService.getRecetas({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
        });
        setItems(data.items);
        setTotalCount(data.totalCount);
      } catch {
        enqueueSnackbar('Error al cargar recetas', { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    const t = setTimeout(() => fetchData(page, rowsPerPage, searchTerm), searchTerm ? 300 : 0);
    return () => clearTimeout(t);
  }, [page, rowsPerPage, searchTerm, fetchData]);

  const handleDelete = async (codigo: string) => {
    if (!window.confirm('¿Eliminar esta receta?')) return;
    try {
      await recetaService.remove(codigo);
      enqueueSnackbar('Receta eliminada', { variant: 'success' });
      fetchData(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al eliminar receta';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Recetas
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/produccion/recetas/nuevo')}
            >
              Nueva receta
            </Button>
          </Stack>

          <TextField
            fullWidth
            placeholder="Buscar por código o descripción"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Producto terminado</TableCell>
                  <TableCell align="right">Componentes</TableCell>
                  <TableCell>Últ. modificación</TableCell>
                  <TableCell align="right">Costo / UM base</TableCell>
                  <TableCell>UM base</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.codigoReceta} hover>
                    <TableCell>{row.codigoReceta}</TableCell>
                    <TableCell>{row.descripcion}</TableCell>
                    <TableCell>
                      {row.codigoProductoTerminado
                        ? `${row.codigoProductoTerminado} — ${row.descripcionProductoTerminado ?? ''}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{row.cantidadComponentes}</TableCell>
                    <TableCell>
                      {row.fechaUltModificacion ? formatDateTime(row.fechaUltModificacion) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {row.costoUnitario != null ? formatCurrency(row.costoUnitario) : '—'}
                    </TableCell>
                    <TableCell>{row.unidadMedidaBase?.trim() || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/produccion/recetas/${encodeURIComponent(row.codigoReceta)}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.codigoReceta)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No hay recetas para mostrar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Filas"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Recetas;
